import { get } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { PlanExtrasType } from 'webapp/enums/enums';

import type BillingPlan from 'webapp/models-custom/billing-plan';
import { ITEM_ID_EXTRA_DS } from 'webapp/models-custom/subscription';

import type { ParsedInvoiceData } from 'webapp/services/billing';

// shown once the billing service has loaded both subscriptions
interface Args {
	previousSubscription: ParsedInvoiceData | null;
	nextSubscription: ParsedInvoiceData | null;
}

export default class BillingCompareSubscriptionsComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;

	get ps() {
		return this.args.previousSubscription!;
	}

	get ns() {
		return this.args.nextSubscription!;
	}

	get psPlanItem() {
		return this.ps.items.find((item) => item.type === 'plan');
	}

	get psPlan(): BillingPlan | null {
		return this.psPlanItem
			? this.billingService.findPlan(this.psPlanItem?.product_price_id)
			: null;
	}

	get nsPlanItem() {
		return this.ns.items.find((item) => item.type === 'plan');
	}

	get psExtraDsFreeItem() {
		return this.ps.items.find(
			(i) =>
				i.type === PlanExtrasType.EXTENSION &&
				i.product_id === ITEM_ID_EXTRA_DS &&
				i.pricePerItem === 0,
		);
	}

	get nsExtraDsFreeItem() {
		return this.ns.items.find(
			(i) =>
				i.type === PlanExtrasType.EXTENSION &&
				i.product_id === ITEM_ID_EXTRA_DS &&
				i.pricePerItem === 0,
		);
	}

	get psExtraDsPaidItem() {
		return this.ps.items.find(
			(i) =>
				i.type === PlanExtrasType.EXTENSION &&
				i.product_id === ITEM_ID_EXTRA_DS &&
				i.pricePerItem > 0,
		);
	}

	get nsExtraDsPaidItem() {
		return this.ns.items.find(
			(i) =>
				i.type === PlanExtrasType.EXTENSION &&
				i.product_id === ITEM_ID_EXTRA_DS &&
				i.pricePerItem > 0,
		);
	}

	get psDiscountItem() {
		return this.ps.items.find((i) => i.type === 'discount');
	}

	get nsDiscountItem() {
		return this.ns.items.find((i) => i.type === 'discount');
	}

	get psAddOnItems() {
		return this.ps.items.filter((i) => i.type === PlanExtrasType.ADD_ON);
	}

	get nsAddOnItemsPrices() {
		return this.ns.items
			.filter((i) => i.type === PlanExtrasType.ADD_ON)
			.reduce(
				(acc, i) => {
					acc[i.product_id] = i.formattedPrice;

					return acc;
				},
				{} as Record<string, string>,
			);
	}
	<template>
		<div class="billing-subscription-changes-compare">
			<div class="subscription previous">
				<div class="plan-card">
					<p class="plan-card__header">Your current subscription</p>
					<p class="plan-card__name">{{this.psPlanItem.name}} - Legacy</p>
					<p class="plan-card__price">{{this.ps.totalMonthlyFormatted}}</p>
				</div>
			</div>
			<div class="table desc">
				<div class="header">
					&nbsp;
				</div>
				<div class="base">
					<strong>Base plan</strong>
					({{this.psPlan.freeSources}}
					data sources incl.)
				</div>
				<div class="extra-ds">
					<p><strong>Additional data sources</strong></p>
					<p class="sub-item">
						{{#if this.psExtraDsFreeItem}}
							{{this.psExtraDsFreeItem.name}}
						{{else}}
							Free data sources
						{{/if}}
					</p>
					<p class="sub-item">
						{{#if this.psExtraDsPaidItem}}
							{{this.psExtraDsPaidItem.name}}
						{{else}}
							Additional data sources
						{{/if}}
					</p>
				</div>
				<div class="savings">
					<strong>Your savings</strong>
				</div>
				{{#each this.psAddOnItems as |addon|}}
					<div class="add-ons">
						<strong>{{addon.name}}</strong>
						add on
					</div>
				{{/each}}
			</div>
			<div class="table previous">
				<div class="header">
					Current plan
				</div>
				<div class="base">{{this.psPlanItem.formattedPrice}}</div>
				<div class="extra-ds">
					<p>
						{{#if this.psExtraDsPaidItem}}
							{{this.psExtraDsPaidItem.formattedPrice}}
						{{else}}
							$0.00
						{{/if}}
					</p>
					<p class="sub-item">
						{{#if this.psExtraDsFreeItem}}
							{{this.psExtraDsFreeItem.shownQuantity}}
							<span>(x $0.00)</span>
						{{else}}
							0
							<span>(x $0.00)</span>
						{{/if}}
					</p>
					<p class="sub-item">
						{{#if this.psExtraDsPaidItem}}
							{{this.psExtraDsPaidItem.shownQuantity}}
							<span>(x {{this.psExtraDsPaidItem.formattedPricePerItem}})</span>
						{{else}}
							0
							<span>(x $0.00)</span>
						{{/if}}
					</p>
				</div>
				<div class="savings">
					{{#if this.psDiscountItem}}
						{{this.psDiscountItem.formattedPrice}}
					{{else}}
						$0.00
					{{/if}}
				</div>
				{{#each this.psAddOnItems as |addon|}}
					<div class="add-ons">
						{{addon.formattedPrice}}
					</div>
				{{/each}}
			</div>
			<div class="subscription next">
				<div class="plan-card">
					<p class="plan-card__header">Your new subscription</p>
					<p class="plan-card__name">{{this.nsPlanItem.name}} - 2024</p>
					<p class="plan-card__price">{{this.ns.totalMonthlyFormatted}}</p>
				</div>
			</div>
			<div class="table desc extra">
				<div class="header">
					&nbsp;
				</div>
				<div class="base">
					<strong>Base plan</strong>
					({{this.psPlan.freeSources}}
					data sources incl.)
				</div>
				<div class="extra-ds">
					<p><strong>Additional data sources</strong></p>
					<p class="sub-item">
						{{#if this.psExtraDsFreeItem}}
							{{this.psExtraDsFreeItem.name}}
						{{else}}
							Free data sources
						{{/if}}
					</p>
					<p class="sub-item">
						{{#if this.psExtraDsPaidItem}}
							{{this.psExtraDsPaidItem.name}}
						{{else}}
							Additional data sources
						{{/if}}
					</p>
				</div>
				<div class="savings">
					<strong>Your savings</strong>
				</div>
				{{#each this.psAddOnItems as |addon|}}
					<div class="add-ons">
						<strong>{{addon.name}}</strong>
						add on
					</div>
				{{/each}}
			</div>
			<div class="table next">
				<div class="header">New plan</div>
				<div class="base">{{this.nsPlanItem.formattedPrice}}</div>
				<div class="extra-ds">
					<p>
						{{#if this.nsExtraDsPaidItem}}
							{{this.nsExtraDsPaidItem.formattedPrice}}
						{{else}}
							$0.00
						{{/if}}
					</p>
					<p class="sub-item">
						{{#if this.nsExtraDsFreeItem}}
							{{this.nsExtraDsFreeItem.shownQuantity}}
							<span>(x {{this.nsExtraDsFreeItem.formattedPricePerItem}})</span>
						{{else}}
							0 (x $0.00)
						{{/if}}
					</p>
					<p class="sub-item">
						{{#if this.nsExtraDsPaidItem}}
							{{this.nsExtraDsPaidItem.shownQuantity}}
							<span>(x {{this.nsExtraDsPaidItem.formattedPricePerItem}})</span>
						{{else}}
							0 (x $0.00)
						{{/if}}
					</p>
				</div>
				<div class="savings">
					{{#if this.nsDiscountItem}}
						{{this.nsDiscountItem.formattedPrice}}
					{{else}}
						$0.00
					{{/if}}
				</div>
				{{#each this.psAddOnItems as |addon|}}
					<div class="add-ons">
						&nbsp;{{get this.nsAddOnItemsPrices addon.product_id}}
					</div>
				{{/each}}
			</div>
		</div>
	</template>
}
